import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, Paper, TextField, IconButton, SvgIcon, Typography, Link } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";

export const FeedbackDialog = ({ open, onClose, feedback, onFeedbackChange, submitted, submitting, error, onSubmit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleContactLinkClick = () => {
    // Close first: the panel is fixed and doesn't unmount on route change,
    // and closing also clears the draft plus its submitted/error flags.
    onClose();
    navigate("/contact");
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: { xs: 72, md: 84 },
        right: { xs: 16, md: 24 },
        width: { xs: "calc(100vw - 32px)", sm: 360 },
        borderRadius: "16px",
        overflow: "hidden",
        zIndex: (t) => t.zIndex.drawer + 1,
        display: "flex",
        flexDirection: "column",
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 0.2s ease, transform 0.25s ease",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: (t) => t.palette.brand2,
          color: "white",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {t("common.feedback")}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
          <SvgIcon fontSize="small">
            <FontAwesomeIcon icon={faXmark} />
          </SvgIcon>
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, bgcolor: "background.paper" }}>
        {submitted ? (
          <Typography sx={{ py: 1 }}>{t("common.feedbackSuccess")}</Typography>
        ) : (
          <>
            <TextField
              autoFocus={open}
              multiline
              rows={4}
              fullWidth
              placeholder={t("common.feedbackPlaceholder")}
              value={feedback}
              onChange={(e) => onFeedbackChange(e.target.value)}
              size="small"
            />
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      {!submitted && (
        <Box sx={{ px: 2, pb: 2, bgcolor: "background.paper" }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onSubmit}
            disabled={!feedback.trim() || submitting}
            sx={{
              bgcolor: (t) => t.palette.brand2,
              "&:hover": { bgcolor: (t) => t.palette.brand2, filter: "brightness(1.15)" },
            }}
          >
            {t("common.feedbackSubmit")}
          </Button>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleContactLinkClick}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "center",
              mt: 1,
              color: "text.secondary",
            }}
          >
            {t("common.feedbackContactLink")}
          </Link>
        </Box>
      )}
    </Paper>
  );
};

FeedbackDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  feedback: PropTypes.string.isRequired,
  onFeedbackChange: PropTypes.func.isRequired,
  submitted: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};
